import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Flex,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  Image,
  Select,
  Textarea,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PAGE_SIZE = 20;
const columns = [
  { key: "cover", label: "커버" },
  { key: "name_album_en", label: "앨범명(EN)" },
  { key: "name_album_kr", label: "앨범명(KR)" },
  { key: "code_album", label: "앨범코드" },
  { key: "label", label: "레이블" },
  { key: "musician", label: "뮤지션" },
  { key: "status_musician", label: "상태" },
  { key: "registered_at", label: "등록일" },
  { key: "published_at", label: "발매일" },
  { key: "actions", label: "관리" },
];

const allEditColumns = [
  { key: "cover", label: "커버" },
  { key: "name_album_en", label: "앨범명(EN)" },
  { key: "name_album_kr", label: "앨범명(KR)" },
  { key: "nation", label: "국가" },
  { key: "published_at", label: "발매일" },
  { key: "referral", label: "추천인(이메일)" },
  { key: "registered_at", label: "등록일" },
  { key: "sales_type", label: "판매타입" },
  { key: "type_album", label: "앨범타입" },
  { key: "uci_album_code", label: "UCI 앨범 코드" },
  { key: "upc", label: "UPC" },
  { key: "label", label: "레이블" },
  { key: "musician", label: "뮤지션" },
  { key: "code_album", label: "앨범코드" },
  { key: "description", label: "설명" },
  { key: "distributor", label: "유통사" },
  { key: "status_musician", label: "상태" },
];

const statusOptions = [
  "[뮤지션] 작성중",
  "[뮤지션] 등록 완료",
  "[뮤지션] 수정 요청",
  "[관리자] 발매 대기",
  "[관리자] 발매 준비 중",
  "[관리자] 발매 신청 완료",
  "[관리자] 발매 완료",
  "[음반] 판매중",
  "[음반] 판매 완료",
];

const salesTypeOptions = ["발매 음반", "판매 음반"];

const albumTypeOptions = [null, "디지털싱글", "정규앨범"];

const AlbumsList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState("");
  const [inputPage, setInputPage] = useState(page.toString());

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const { data: rows, error } = await supabase.rpc(
      "get_albums_with_label_and_musician",
      {
        status: statusFilter || null,
        search: search || null,
        page: page,
      }
    );

    if (!error) {
      setData(rows || []);
      // 전체 페이지 수를 계산하기 위해 전체 개수를 가져옵니다
      let countQuery = supabase.from("album").select("*", { count: "exact" });

      if (search) {
        countQuery = countQuery.or(
          `name_album_kr.ilike.%${search}%,code_album.ilike.%${search}%`
        );
      }
      if (statusFilter) {
        countQuery = countQuery.eq("status_musician", statusFilter);
      }

      const { count } = await countQuery;
      setTotalPages(Math.max(1, Math.ceil((count || 1) / PAGE_SIZE)));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [search, page, statusFilter]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleRowClick = (row) => {
    setSelected(row);
    // label_name과 musician_name을 제외한 필드만 저장
    const { label_name, musician_name, ...editData } = row;
    setEditFields(editData);
    setModalOpen(true);
  };

  const handleEditChange = (key, value) => {
    setEditFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selected) return;
    const { error } = await supabase
      .from("album")
      .update(editFields)
      .eq("id", selected.id);
    if (!error) {
      toast({ title: "수정 완료", status: "success" });
      setModalOpen(false);
      fetchData();
    } else {
      toast({
        title: "수정 실패",
        status: "error",
        description: error.message,
      });
    }
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `album/${selected.id}/${
        selected.code_album
      }_IMG_${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("image")
        .upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: urlData, error: urlError } = await supabase.storage
        .from("image")
        .createSignedUrl(fileName, 60 * 60 * 24 * 7);
      if (urlError || !urlData?.signedUrl) throw urlError;
      setEditFields((prev) => ({ ...prev, cover: urlData.signedUrl }));
      toast({ title: "커버 업로드 완료", status: "success" });
    } catch (error) {
      toast({ title: "커버 업로드 실패", status: "error" });
    }
  };

  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };

  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  const generateAlbumCode = () => {
    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    const seconds = String(today.getSeconds()).padStart(2, "0");

    const albumCode = `${year}${month}${day}A${hours}${minutes}${seconds}`;
    setGeneratedCode(albumCode);
    setCodeModalOpen(true);
  };

  return (
    <Box maxW="1400px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center">
        <Input
          placeholder="앨범명(KR), 앨범코드, 레이블, 뮤지션 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          width="320px"
        />
        <Select
          placeholder="상태 전체"
          width="200px"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          {statusOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Select>
        <Button colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
        <Button colorScheme="green" onClick={generateAlbumCode} ml="auto">
          앨범코드 생성
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              {columns.map((col) => (
                <Th key={col.key}>{col.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Text color="gray.400">데이터 없음</Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => (
                <Tr key={row.id}>
                  {columns.map((col) => (
                    <Td
                      key={col.key}
                      maxW="200px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {col.key === "actions" ? (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(row);
                          }}
                        >
                          수정
                        </Button>
                      ) : col.key === "cover" || col.key === "cover_url" ? (
                        <Image
                          src={row.cover || row.cover_url}
                          alt="cover"
                          boxSize="48px"
                          borderRadius="md"
                        />
                      ) : col.key === "published_at" ||
                        col.key === "registered_at" ? (
                        row[col.key] ? (
                          new Date(row[col.key]).toLocaleDateString()
                        ) : (
                          "-"
                        )
                      ) : col.key === "label" ? (
                        row.label_name || "-"
                      ) : col.key === "musician" ? (
                        row.musician_name || "-"
                      ) : (
                        row[col.key] ?? "-"
                      )}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>
      {/* 수정 모달 */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>앨범 정보 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {allEditColumns.map((col) => (
              <Flex key={col.key} align="center" mb={3}>
                <Box minW="120px">{col.label}</Box>
                {col.key === "cover" ? (
                  <Flex align="center" gap={2}>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCoverUpload(e.target.files[0])}
                    />
                    {editFields.cover && (
                      <Image
                        src={editFields.cover}
                        alt="cover"
                        boxSize="48px"
                        borderRadius="md"
                      />
                    )}
                  </Flex>
                ) : col.key === "label" ? (
                  <Box w="100%">
                    <Input
                      value={editFields.label || ""}
                      onChange={(e) =>
                        handleEditChange("label", e.target.value)
                      }
                      placeholder="레이블 ID 입력"
                      mb={1}
                    />
                    <Input
                      value={selected?.label_name}
                      isReadOnly
                      mb={1}
                      bg="gray.50"
                    />
                  </Box>
                ) : col.key === "musician" ? (
                  <Box w="100%">
                    <Input
                      value={editFields.musician || ""}
                      onChange={(e) =>
                        handleEditChange("musician", e.target.value)
                      }
                      placeholder="뮤지션 ID 입력"
                      mb={1}
                    />
                    <Input
                      value={selected?.musician_name}
                      isReadOnly
                      mb={1}
                      bg="gray.50"
                    />
                  </Box>
                ) : col.key === "description" ? (
                  <Textarea
                    value={editFields[col.key] ?? ""}
                    onChange={(e) => handleEditChange(col.key, e.target.value)}
                    minH="80px"
                  />
                ) : col.key === "published_at" ||
                  col.key === "registered_at" ? (
                  <Input
                    type="date"
                    value={editFields[col.key]?.slice(0, 10) || ""}
                    onChange={(e) => handleEditChange(col.key, e.target.value)}
                  />
                ) : col.key === "status_musician" ? (
                  <Select
                    value={editFields[col.key] ?? statusOptions[0]}
                    onChange={(e) => handleEditChange(col.key, e.target.value)}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Select>
                ) : col.key === "sales_type" ? (
                  <Select
                    value={editFields[col.key] ?? salesTypeOptions[0]}
                    onChange={(e) => handleEditChange(col.key, e.target.value)}
                  >
                    {salesTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Select>
                ) : col.key === "type_album" ? (
                  <Select
                    value={editFields[col.key] ?? ""}
                    onChange={(e) =>
                      handleEditChange(col.key, e.target.value || null)
                    }
                  >
                    <option value="">선택안함</option>
                    {albumTypeOptions.filter(Boolean).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    value={editFields[col.key] ?? ""}
                    onChange={(e) => handleEditChange(col.key, e.target.value)}
                  />
                )}
              </Flex>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleSave}>
              저장
            </Button>
            <Button ml={3} onClick={() => setModalOpen(false)}>
              취소
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* 앨범코드 생성 모달 */}
      <Modal isOpen={codeModalOpen} onClose={() => setCodeModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>앨범코드 생성</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xl" fontWeight="bold" textAlign="center">
              {generatedCode}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => {
                navigator.clipboard.writeText(generatedCode);
                toast({
                  title: "앨범코드가 복사되었습니다.",
                  status: "success",
                  duration: 2000,
                });
              }}
            >
              복사하기
            </Button>
            <Button ml={3} onClick={() => setCodeModalOpen(false)}>
              닫기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AlbumsList;
